import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import type { Submission, BDRSummary } from "@shared/types";
import { SubmissionSchema, type SubmissionInput } from "@shared/schemas";
import {
  CheckCircle2,
  AlertCircle,
  Info,
  User,
  Building2,
  Calendar,
  FileText,
  SendIcon,
  Sparkles,
} from "lucide-react";

interface Props {
  bdr: BDRSummary;
  prefill?: Partial<Submission>;
  confidence?: Record<string, number>;
  onSubmit: (s: Submission) => void;
}

function uuid() {
  return crypto.randomUUID();
}

function browserTimezone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "America/New_York"; }
}

function domainFromEmail(email: string | undefined): string {
  if (!email) return "";
  const at = email.lastIndexOf("@");
  return at >= 0 ? email.slice(at + 1).toLowerCase() : "";
}

function ConfidenceIcon({ score }: { score: number | undefined }) {
  if (score === undefined) return null;
  if (score >= 0.8) return <CheckCircle2 className="text-[#05FAFC] shrink-0" size={14} aria-label="high confidence" />;
  if (score >= 0.6) return <Info className="text-mustard shrink-0" size={14} aria-label="medium confidence" />;
  return <AlertCircle className="text-red-400 shrink-0" size={14} aria-label="low confidence — please review" />;
}

// ─── Section Header ───────────────────────────────────────────────────────
function SectionHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/[0.08] flex items-center justify-center text-[#7573F7]">
        {icon}
      </div>
      <h2 className="text-base font-extrabold text-white tracking-tight">{title}</h2>
      <div className="flex-1 h-px bg-gradient-to-r from-white/10 to-transparent" />
    </div>
  );
}

// ─── Field Wrapper ────────────────────────────────────────────────────────
interface FieldProps {
  label: string;
  conf?: number;
  error?: string;
  children: React.ReactNode;
}

function Field({ label, conf, error, children }: FieldProps) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-xs font-bold text-white/40 uppercase tracking-widest mb-1.5">
        {label}
        <ConfidenceIcon score={conf} />
      </label>
      {children}
      {error && (
        <div className="text-red-400 text-xs mt-1 font-medium flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </div>
      )}
    </div>
  );
}

// ─── Dark input class ─────────────────────────────────────────────────────
const darkInput = [
  "w-full px-4 py-2.5 rounded-xl",
  "bg-white/[0.03] border border-white/[0.08]",
  "text-white/90 text-sm placeholder:text-white/20",
  "focus:outline-none focus:border-[#7573F7]/40 focus:bg-white/[0.05]",
  "focus:ring-1 focus:ring-[#7573F7]/20",
  "hover:border-white/[0.12] hover:bg-white/[0.04]",
  "transition-all duration-200",
].join(" ");

const darkTextarea = [
  darkInput,
  "resize-none",
].join(" ");

const darkSelect = [
  darkInput,
  "[&>option]:bg-[#0A0118] [&>option]:text-white",
].join(" ");

// ─── Main Form ────────────────────────────────────────────────────────────
export function SubmissionForm({ bdr, prefill, confidence, onSubmit }: Props) {
  const idempotency = useMemo(() => prefill?.idempotency_key ?? uuid(), [prefill?.idempotency_key]);

  const defaults: SubmissionInput = {
    bdr_id: bdr.id,
    idempotency_key: idempotency,
    prospect: {
      first_name: prefill?.prospect?.first_name ?? "",
      last_name: prefill?.prospect?.last_name ?? "",
      email: prefill?.prospect?.email ?? "",
      email_secondary: prefill?.prospect?.email_secondary ?? "",
      title: prefill?.prospect?.title ?? "",
      phone: prefill?.prospect?.phone ?? "",
      linkedin_url: prefill?.prospect?.linkedin_url ?? "",
    },
    company: {
      name: prefill?.company?.name ?? "",
      domain: prefill?.company?.domain ?? domainFromEmail(prefill?.prospect?.email),
      website: prefill?.company?.website ?? "",
      industry: prefill?.company?.industry ?? "",
    },
    meeting: {
      date_local: prefill?.meeting?.date_local ?? "",
      time_local: prefill?.meeting?.time_local ?? "",
      timezone: prefill?.meeting?.timezone ?? browserTimezone(),
      meeting_link: prefill?.meeting?.meeting_link ?? "",
    },
    context: {
      handoff_notes: prefill?.context?.handoff_notes ?? "",
      source_campaign: prefill?.context?.source_campaign ?? "",
      qualification: prefill?.context?.qualification ?? {},
    },
    attachments: prefill?.attachments,
  };

  const [submitErrors, setSubmitErrors] = useState<string[]>([]);

  const { register, handleSubmit, formState: { errors, isSubmitting } } =
    useForm<SubmissionInput>({
      resolver: zodResolver(SubmissionSchema),
      defaultValues: defaults,
      mode: "onTouched",
    });

  function flattenErrors(obj: Record<string, any>, prefix = ""): string[] {
    const msgs: string[] = [];
    for (const [key, val] of Object.entries(obj)) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (val?.message) msgs.push(`${path}: ${val.message}`);
      else if (typeof val === "object" && val !== null) msgs.push(...flattenErrors(val, path));
    }
    return msgs;
  }

  return (
    <motion.form
      noValidate
      onSubmit={handleSubmit(
        (d) => { setSubmitErrors([]); onSubmit(d as Submission); },
        (errs) => { setSubmitErrors(flattenErrors(errs)); }
      )}
      className="relative backdrop-blur-2xl bg-white/[0.02] rounded-2xl border border-white/[0.05] shadow-2xl p-6 sm:p-8 space-y-8"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      {/* ── Prospect ─────────────────────────────────────────────── */}
      <section>
        <SectionHeader icon={<User className="w-4 h-4" />} title="Prospect" />
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="First name" conf={confidence?.["prospect.first_name"]} error={errors.prospect?.first_name?.message}>
            <input className={darkInput} {...register("prospect.first_name")} />
          </Field>
          <Field label="Last name" conf={confidence?.["prospect.last_name"]} error={errors.prospect?.last_name?.message}>
            <input className={darkInput} {...register("prospect.last_name")} />
          </Field>
          <Field label="Work email" conf={confidence?.["prospect.email"]} error={errors.prospect?.email?.message}>
            <input type="email" className={darkInput} {...register("prospect.email")} />
          </Field>
          <Field label="Secondary email (optional)" conf={confidence?.["prospect.email_secondary"]}>
            <input type="email" className={darkInput} {...register("prospect.email_secondary")} />
          </Field>
          <Field label="Title" conf={confidence?.["prospect.title"]}>
            <input className={darkInput} {...register("prospect.title")} />
          </Field>
          <Field label="Phone" conf={confidence?.["prospect.phone"]}>
            <input className={darkInput} {...register("prospect.phone")} />
          </Field>
          <div className="md:col-span-2">
            <Field label="LinkedIn URL" conf={confidence?.["prospect.linkedin_url"]} error={errors.prospect?.linkedin_url?.message}>
              <input className={darkInput} placeholder="https://linkedin.com/in/…" {...register("prospect.linkedin_url")} />
            </Field>
          </div>
        </div>
      </section>

      {/* ── Company ──────────────────────────────────────────────── */}
      <section>
        <SectionHeader icon={<Building2 className="w-4 h-4" />} title="Company" />
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Company name" conf={confidence?.["company.name"]} error={errors.company?.name?.message}>
            <input className={darkInput} {...register("company.name")} />
          </Field>
          <Field label="Domain" conf={confidence?.["company.domain"]} error={errors.company?.domain?.message}>
            <input className={darkInput} placeholder="acme.com" {...register("company.domain")} />
          </Field>
          <Field label="Website (optional)" conf={confidence?.["company.website"]} error={errors.company?.website?.message}>
            <input className={darkInput} placeholder="https://www.acme.com" {...register("company.website")} />
          </Field>
          <Field label="Industry (optional)" conf={confidence?.["company.industry"]}>
            <input className={darkInput} {...register("company.industry")} />
          </Field>
        </div>
      </section>

      {/* ── Meeting ──────────────────────────────────────────────── */}
      <section>
        <SectionHeader icon={<Calendar className="w-4 h-4" />} title="Discovery Meeting" />
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Date" conf={confidence?.["meeting.date_local"]} error={errors.meeting?.date_local?.message}>
            <input type="date" className={`${darkInput} [color-scheme:dark]`} {...register("meeting.date_local")} />
          </Field>
          <Field label="Time (local)" conf={confidence?.["meeting.time_local"]} error={errors.meeting?.time_local?.message}>
            <input type="time" className={`${darkInput} [color-scheme:dark]`} {...register("meeting.time_local")} />
          </Field>
          <Field label="Timezone" conf={confidence?.["meeting.timezone"]} error={errors.meeting?.timezone?.message}>
            <select className={darkSelect} {...register("meeting.timezone")}>
              <option value="America/New_York">Eastern</option>
              <option value="America/Chicago">Central</option>
              <option value="America/Denver">Mountain</option>
              <option value="America/Los_Angeles">Pacific</option>
              <option value="Europe/London">London</option>
              <option value="Europe/Paris">Paris</option>
              <option value="Europe/Berlin">Berlin</option>
              <option value="Asia/Singapore">Singapore</option>
              <option value="Asia/Tokyo">Tokyo</option>
              <option value="Australia/Sydney">Sydney</option>
            </select>
          </Field>
          <div className="md:col-span-3">
            <Field label="Meeting link (optional)" conf={confidence?.["meeting.meeting_link"]} error={errors.meeting?.meeting_link?.message}>
              <input className={darkInput} placeholder="https://meet.google.com/…" {...register("meeting.meeting_link")} />
            </Field>
          </div>
        </div>
      </section>

      {/* ── Handoff notes ────────────────────────────────────────── */}
      <section>
        <SectionHeader icon={<FileText className="w-4 h-4" />} title="Handoff Notes" />
        <Field label="Conversation summary / notes" conf={confidence?.["context.handoff_notes"]}>
          <textarea rows={5} className={darkTextarea} {...register("context.handoff_notes")} />
        </Field>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <Field label="Current solution (optional)">
            <input className={darkInput} {...register("context.qualification.current_solution")} />
          </Field>
          <Field label="Timeframe (optional)">
            <input className={darkInput} {...register("context.qualification.timeframe")} />
          </Field>
        </div>
      </section>

      {/* ── Validation errors ────────────────────────────────────── */}
      {submitErrors.length > 0 && (
        <motion.div
          className="bg-red-500/10 border border-red-500/30 rounded-xl p-4"
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-sm font-bold text-red-400 mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Please fix {submitErrors.length} {submitErrors.length === 1 ? "error" : "errors"}:
          </div>
          <ul className="space-y-1 text-xs text-white/60 pl-6 list-disc">
            {submitErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </motion.div>
      )}

      {/* ── Footer ───────────────────────────────────────────────── */}
      <div className="flex items-center justify-between pt-3 border-t border-white/[0.05]">
        <div className="flex items-center gap-2 text-white/20 text-xs">
          <Sparkles className="w-3 h-3" />
          <span className="font-mono">{idempotency.slice(0, 8)}</span>
        </div>

        <motion.button
          type="submit"
          disabled={isSubmitting}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
          className={`px-6 py-3 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${
            isSubmitting
              ? "bg-white/[0.05] text-white/30 cursor-not-allowed"
              : "bg-white text-[#0A0118] shadow-lg shadow-white/10 hover:shadow-white/20"
          }`}
        >
          <SendIcon className="w-4 h-4" />
          {isSubmitting ? "Submitting…" : "Register meeting"}
        </motion.button>
      </div>
    </motion.form>
  );
}
